import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ExtendedContentInsertVariable } from 'api/graphQL/types/contentInsert.types'
import { ClassicEditor } from 'ckeditor5'
import { useNodeKeySelectionStore } from 'components/WorkspaceEditor/NodeKeySelectionStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    findRootArticleNode,
    getContentOwnerType,
    getParentKeyChain,
} from 'components/WorkspaceEditor/utils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { PRODUCT_TYPE_PROPERTY_NODE_TYPE } from '../../../features/bestPractices/constants'
import { findNodeById } from '../../../features/promoteDemote/utils/nodeHelpers'
import { useCopyPasteShallowSelector } from '../store/CopyPasteStore'

interface CKEditorPasteEvent {
    stop: () => void
}

interface CKEditorPasteData {
    dataTransfer: {
        getData: (format: string) => string
        types?: string[]
    }
}

/** Store state read live by handlePaste at paste time, passed through to
 * handleHtmlParagraphPasteFromInput instead of being closed over. */
interface PasteStateSnapshot {
    htmlContent: string
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    textContentElementToAdd: Partial<TextContentElement> | null
}

const HTML_DETECTION = {
    PARAGRAPH_TAG: '<p',
    LINE_BREAK_TAG: '<br',
} as const

/** Tags that end a visual line in Word's HTML: soft breaks and block closers. */
const LINE_BOUNDARY_TAG = /<br\s*\/?>|<\/p\s*>|<\/div\s*>/gi

/**
 * Visual lines of decoded Word HTML, empty ones dropped. Uses the HTML because Word
 * does not always separate a soft line break in plain text, but always emits `<br>`.
 *
 * @param decodedHtml - HTML already passed through decodeHtmlEntities
 */
const getContentLines = (decodedHtml: string): string[] =>
    decodedHtml
        // Word wraps its source; those newlines are not line boundaries.
        .replace(/[\r\n]+/g, ' ')
        .replace(LINE_BOUNDARY_TAG, '\n')
        // Entities are already decoded, so stripping tags leaves the text.
        .replace(/<[^>]*>/g, '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)

/** Leading Word list marker ("1.", "A.", "iv.", or a bullet) plus its whitespace. */
const WORD_LIST_MARKER_PREFIX = /^\s*(?:[A-Za-z]\.|\d+\.|[ivxlcdm]+\.|[•*-])\s+/i

/**
 * Sanitizes and decodes HTML content, especially from Microsoft Word.
 * Handles fragment extraction, entity decoding, and removes Word-specific formatting.
 */
const decodeHtmlEntities = (html: string): string => {
    if (!html) return html

    let processedHtml = html

    // Extract Word fragment content if present
    const fragmentMatch = processedHtml.match(
        /<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/
    )
    if (fragmentMatch) {
        processedHtml = fragmentMatch[1]
    }

    // Use browser's built-in HTML entity decoding
    const textarea = document.createElement('textarea')
    textarea.innerHTML = processedHtml
    processedHtml = textarea.value

    return (
        processedHtml
            // Remove MSO conditional comments
            .replace(/<!\[if !supportLists\]>[\s\S]*?<!\[endif\]>/g, '')
            // Remove MSO-specific tags
            .replace(/<\/?o:p[^>]*>|<a\s+name="[^"]*"[^>]*>|<\/a>/g, '')
            // Clean up Word list numbering patterns
            .replace(
                /(^|>)\s*(?:[A-Za-z]\.|\d+\.|[ivxlcdm]+\.|[•\*\-])\s+/gi,
                '$1'
            )
    )
}

/**
 * Counts real paragraph ancestors by walking dtvContentItemParentId links.
 * Stops at the owning Nugget/container, that's not a paragraph level.
 */
const findFlatParentChainDepth = (
    nodeId: string,
    nodes: Partial<ContentItemViewNodesType>[]
): number => {
    const visited = new Set<string>()
    let current = nodes.find((n) => n.id === nodeId)
    let depth = 0

    while (current?.dtvContentItemParentId) {
        visited.add(current.id ?? '')
        const parentId = current.dtvContentItemParentId
        const parent = nodes.find((n) => n.id === parentId)

        if (
            !parent ||
            parent.partType === 'Part' ||
            visited.has(parent.id ?? '')
        ) {
            return depth
        }

        depth += 1
        current = parent
    }
    return depth
}

/** Pure, takes the live snapshot as arguments (see handleHtmlParagraphPasteFromInput). */
const getStartingLevel = (
    node: Partial<ContentItemViewNodesType>,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
): number => {
    if (!node.id) return 1
    return findFlatParentChainDepth(node.id, contentItemViewNodes) + 1
}

/** Same reasoning as getStartingLevel, pure, takes a live snapshot as an argument. */
const getPasteIndex = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    node: Partial<ContentItemViewNodesType>
): number => {
    // If pasting into an existing paragraph, use order + 1 to place content after it
    if (node.order !== undefined) {
        return node.order + 1
    }

    // Fallback: Find all siblings and insert at the end
    const siblings = contentItemViewNodes
        .filter(
            (ci) =>
                ci.partType === node.partType &&
                ci.textType === node.textType &&
                ci.dtvParentId === node.dtvParentId
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    return siblings.length
}

export const useHtmlParagraphPaste = () => {
    const { projectId } = useParams<{
        projectId: string
    }>()
    const selectedProjectElementNode = useShallowSelector(
        (state) => state.selectedProjectElementNode
    )
    const productTypeNuggetId = useMemo(() => {
        const isProductType =
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTTYPE
        return isProductType
            ? selectedProjectElementNode?.elementId ??
                  selectedProjectElementNode?.baseElementId
            : null
    }, [selectedProjectElementNode])

    const { openPasteDialog } = useCopyPasteShallowSelector((state) => ({
        openPasteDialog: state.openPasteDialog,
    }))

    const handleHtmlParagraphPaste = useCallback(
        ({
            rootArticleContentItemIds,
            ...rest
        }: ExtendedContentInsertVariable) => {
            openPasteDialog({
                input: {
                    ...rest,
                    projectId,
                    productTypeNuggetId: productTypeNuggetId,
                },
                partType: rest.partType,
                productFamilyId: rest.productFamilyId,
                rootArticleContentItemIds: rootArticleContentItemIds ?? [],
            })
        },
        [openPasteDialog, projectId, productTypeNuggetId]
    )

    // Takes state as arguments, read live by handlePaste at paste time. CKEditor binds
    // this handler once and never rebinds it, so a closed-over value would go stale.
    const handleHtmlParagraphPasteFromInput = useCallback(
        ({
            htmlContent,
            contentItemViewNodes,
            textContentElementToAdd,
        }: PasteStateSnapshot) => {
            const { selectedKey } = useNodeKeySelectionStore.getState()
            // pasteNode may have moved via promote/demote, use it for position.
            // anchorNode (the clicked row) stays the source for field/owner context.
            const pasteNode = textContentElementToAdd?.id
                ? findNodeById(contentItemViewNodes, textContentElementToAdd.id)
                : undefined
            const anchorNode = contentItemViewNodes.find(
                (n) => n.id === selectedKey
            )
            const node = pasteNode ?? anchorNode
            if (!anchorNode || !node?.elements) return

            const rootArticleId = findRootArticleNode(
                contentItemViewNodes,
                anchorNode.id ?? ''
            )?.id

            const parentNode = contentItemViewNodes?.find((item) =>
                anchorNode.dtvContentItemParentId
                    ? item.id === anchorNode.dtvContentItemParentId
                    : item.dtvItemId === anchorNode.dtvParentId
            )

            const isProductTypeProperty =
                anchorNode.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE ||
                parentNode?.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE

            // contentItemParentType must match what contentItemParentId resolves to,
            // or the backend rejects a Nugget id mislabeled as a ContentItem id.
            // Resolved from node (not anchorNode) so it stays consistent with
            // contentItemParentId when pasteNode is a different node than anchorNode.
            const contentItemParentId =
                node.dtvContentItemParentId || node.dtvParentId || null
            const contentItemParentType = node.dtvContentItemParentId
                ? 'ContentItem'
                : contentItemViewNodes?.find(
                      (item) => item.dtvItemId === node.dtvParentId
                  )?.type ?? 'None'

            const ownerConfig = isProductTypeProperty
                ? {
                      contentItemOwnerType: 'ProductTypeProperty',
                      contentItemOwnerId: null,
                  }
                : {
                      contentItemOwnerType: getContentOwnerType(
                          parentNode,
                          anchorNode
                      ),
                      contentItemOwnerId:
                          anchorNode.propertyRefId ||
                          anchorNode.dtvItemId ||
                          null,
                  }

            handleHtmlParagraphPaste?.({
                html: htmlContent,
                index: Math.max(
                    1,
                    pasteNode
                        ? pasteNode.order ?? 0
                        : getPasteIndex(contentItemViewNodes, node)
                ),
                contentItemParentId,
                contentItemParentType,
                ...ownerConfig,
                productFamilyId: anchorNode.productFamilyId ?? '',
                productTypePropertyType: anchorNode.isSharedProductTypeProperty
                    ? 'Shared'
                    : 'Specific',
                startingLevel: getStartingLevel(node, contentItemViewNodes),
                partType: anchorNode.partType?.toUpperCase() ?? '',
                rootArticleContentItemIds: rootArticleId ? [rootArticleId] : [],
                parentPropertyKey:
                    getParentKeyChain(contentItemViewNodes, node) || null,
            })
        },
        [handleHtmlParagraphPaste]
    )

    /**
     * Handles CKEditor paste events for HTML paragraphs. Multi-line/HTML content is
     * allowed only when adding new content; a single visual line pasted into existing
     * content is inserted as plain text instead of being blocked.
     *
     * @param editor - needed to insert a single-line paste at the cursor
     */
    const handlePaste = useCallback(
        (
            evt: CKEditorPasteEvent,
            data: CKEditorPasteData,
            editor?: ClassicEditor
        ) => {
            const pastedHtml = data.dataTransfer.getData('text/html')
            const pastedText = data.dataTransfer.getData('text/plain')

            const isMultiLineContent =
                pastedText &&
                (pastedText.includes('\n') || pastedText.includes('\r'))
            const lowercasedHtml = pastedHtml.toLowerCase()
            // Rich formatting, so it follows multi-paragraph rules instead of the native
            // paste, which corrupts saved content.
            const hasLineBreakTag =
                lowercasedHtml.indexOf(HTML_DETECTION.LINE_BREAK_TAG) !== -1
            const hasRichFormatting =
                pastedHtml &&
                (lowercasedHtml.indexOf(HTML_DETECTION.PARAGRAPH_TAG) !== -1 ||
                    hasLineBreakTag) &&
                data.dataTransfer.types?.some(
                    (type: string) => type?.toLowerCase().trim() === 'text/html'
                )

            // Only intercept paste for multi-line content or rich formatting
            if (!(isMultiLineContent || hasRichFormatting)) return

            // Read live, before the revert below changes the store.
            const {
                contentItemViewNodes,
                textContentElementToAdd,
                previousContentItemViewNodes,
                actions: {
                    setContentItemViewNodes,
                    setTextContentElementToAdd,
                },
            } = useWorkspace2Store.getState()

            // Rich formatting into existing content is blocked, except a single visual
            // line: Word's trailing empty line is dropped and the rest inserted at the cursor.
            if (hasRichFormatting && !textContentElementToAdd) {
                evt.stop()
                const contentLines = getContentLines(
                    decodeHtmlEntities(pastedHtml)
                )
                const singleLineText =
                    contentLines.length === 1
                        ? contentLines[0].replace(WORD_LIST_MARKER_PREFIX, '')
                        : ''

                if (editor && singleLineText) {
                    // insertText (not a model write) so the text gets TextIdPlugin's `id`,
                    // required by the save-time parse, and flows through track changes.
                    // Deferred: the view is still mid-update here (view-renderer-filler-was-lost).
                    setTimeout(() => {
                        if (editor.state === 'destroyed') return // execute() isn't safe after destroy
                        editor.execute('insertText', { text: singleLineText })
                    }, 0)
                }
                return
            }

            evt.stop()

            // Remove the empty container that was optimistically added to prevent it from showing up after paste
            if (textContentElementToAdd && previousContentItemViewNodes) {
                setContentItemViewNodes(previousContentItemViewNodes)
                setTextContentElementToAdd(null)
            }

            // Decode HTML entities while preserving HTML structure
            const htmlContent = pastedHtml
                ? decodeHtmlEntities(pastedHtml)
                : pastedText
            handleHtmlParagraphPasteFromInput({
                htmlContent,
                contentItemViewNodes,
                textContentElementToAdd,
            })
        },
        [handleHtmlParagraphPasteFromInput]
    )

    return {
        handlePaste,
    }
}
